import React, { useState } from 'react';
import { useDispatch } from 'react-redux';

import {
  capitalize,
  Checkbox,
  Divider,
  Grid,
  Hidden,
  Stack,
  Typography,
} from '@mui/material';

import ConfirmationDialog from 'commons/components/ConfirmationDialog/ConfirmationDialog.component';
import { InboundTaskItemStatusEnum } from 'commons/enums';
import {
  PRIMARY_BLACK,
  PRIMARY_BLUE,
  PRIMARY_RED,
  PRIMARY_YELLOW,
} from 'commons/styles/colors';
import { IInboundTaskItem } from 'commons/types';
import { AppDispatch } from 'redux-stores/store';

import { ItemDialogConfig } from '../configs';
import { ItemDialogConfigEnum } from '../enums';
import S from '../InboundInvoiceApprovalItemPage.styles';
import { IItemDialogData } from '../interfaces';

export const ItemRow: React.FC<{
  item: IInboundTaskItem;
  inboundTaskNumber: string;
  invoiceNumber: string;
  onCheckboxClick: (skuCode: string, foc: boolean) => void;
  checked: boolean;
  handleSetItemDetail: (value: IInboundTaskItem) => void;
  index: number;
}> = ({
  item,
  inboundTaskNumber,
  invoiceNumber,
  onCheckboxClick,
  checked,
  handleSetItemDetail,
  index,
}) => {
  const dispatch = useDispatch<AppDispatch>();

  const [openItemDialog, setOpenItemDialog] = useState<boolean>(false);
  const [openItemDialogData, setOpenItemDialogData] =
    useState<IItemDialogData | null>(null);

  const handleOpenItemDialog = (name: ItemDialogConfigEnum): void => {
    setOpenItemDialogData(ItemDialogConfig[name]);
    setOpenItemDialog(true);
  };
  const handleCloseItemDialog = (): void => {
    setOpenItemDialogData(null);
    setOpenItemDialog(false);
  };

  const totalReceivedQty = item.receive_actual.reduce(
    (receivedQty, receiveActual) => receivedQty + receiveActual.quantity,
    0,
  );
  const qtyColor =
    totalReceivedQty !== item.quantity ? PRIMARY_YELLOW : PRIMARY_BLACK;
  const statusColor =
    item.status === InboundTaskItemStatusEnum.APPROVED
      ? PRIMARY_BLUE
      : item.status === InboundTaskItemStatusEnum.REJECTED
      ? PRIMARY_RED
      : PRIMARY_BLACK;

  return (
    <>
      <S.TaskItemWrapper
        container
        key={`inbound-invoice-approval-header-${index}`}
      >
        <Grid item xs={1.5} sm={1}>
          <Checkbox
            data-testid={`checkboxItemInboundApproval{${index + 1}}`}
            onClick={() => onCheckboxClick(item.sku_code, item.foc)}
            checked={checked}
          />
        </Grid>
        <S.TaskItemText item xs={3} sm={2}>
          <Stack spacing={1}>
            {item.sku_code}
            {item.foc && <S.FOCText>FOC</S.FOCText>}
          </Stack>
        </S.TaskItemText>
        <Hidden only="xs">
          <S.TaskItemText item xs={2}>
            {item.name}
          </S.TaskItemText>
          <S.TaskItemText item xs={1.5}>
            {item.uom}
          </S.TaskItemText>
        </Hidden>
        <S.TaskItemText item xs={1.5} sm={1}>
          <div style={{ textAlign: 'center', paddingRight: 8 }}>
            <span style={{ color: qtyColor }}>{totalReceivedQty}</span>/
            {item.quantity}
          </div>
        </S.TaskItemText>
        <S.TaskItemText item xs={2.5} sm={1.5} style={{ color: statusColor }}>
          {item.status === InboundTaskItemStatusEnum.RECEIVED
            ? '-'
            : capitalize(item.status)}
        </S.TaskItemText>
        <Grid container item xs={3} spacing={1}>
          <Grid item xs={6} style={{ textAlign: 'center' }}>
            <S.RowButton
              sx={{
                width: { xs: '48px', sm: '100%' },
                minWidth: { xs: '48px', sm: 'auto' },
              }}
              className="reject"
              onClick={() => handleOpenItemDialog(ItemDialogConfigEnum.REJECT)}
            >
              <Typography sx={{ display: { xs: 'block', sm: 'none' } }}>
                ✘
              </Typography>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: '14px',
                  display: { xs: 'none', sm: 'block' },
                }}
              >
                Reject
              </Typography>
            </S.RowButton>
          </Grid>
          <Grid item xs={6} style={{ textAlign: 'center' }}>
            <S.RowButton
              sx={{
                width: { xs: '48px', sm: '100%' },
                minWidth: { xs: '48px', sm: 'auto' },
              }}
              onClick={() => handleOpenItemDialog(ItemDialogConfigEnum.APPROVE)}
            >
              <Typography sx={{ display: { xs: 'block', sm: 'none' } }}>
                ✔
              </Typography>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: '14px',
                  display: { xs: 'none', sm: 'block' },
                }}
              >
                Approve
              </Typography>
            </S.RowButton>
          </Grid>
          <Grid item xs={12} style={{ textAlign: 'center' }}>
            <S.RowButton
              sx={{
                width: { xs: '100%' },
              }}
              onClick={() => handleSetItemDetail(item)}
            >
              Details
            </S.RowButton>
          </Grid>
        </Grid>
      </S.TaskItemWrapper>
      <Divider />
      {/* Single item confirmation dialog */}
      <ConfirmationDialog
        title={openItemDialogData?.title || ''}
        content={openItemDialogData?.content(item.sku_code) || ''}
        onClose={() => handleCloseItemDialog()}
        openModal={openItemDialog}
        onConfirm={() =>
          openItemDialogData?.onConfirm(
            dispatch,
            inboundTaskNumber,
            invoiceNumber,
            item.sku_code,
            item.foc,
          )
        }
      />
    </>
  );
};
